import { escapeRegExp, uniq } from "lodash-es";

import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";

// Mirrors the server's own pipeline (`word-cloud-helper.ts`): a note is split on clause breaks, and
// each clause is then tokenized by replacing every character outside \p{L}\p{M} with a space. So a
// counted form's words may be separated by any run of non-letters — digits included, `patient 2024
// outcome` is counted — but never across a clause break, which ends the phrase server-side.
const CLAUSE_BREAKS = ".,!?;:\\n\\r";
const LETTER = "\\p{L}\\p{M}";
const SEPARATOR = `[^${LETTER}${CLAUSE_BREAKS}]+`;
// Standalone — "car" must not hit "carrier". Only letters bound a word: the tokenizer turns a
// neighbouring digit into a space, so "outcome2024" was counted as "outcome". The class mirrors the
// server tokenizer exactly — it counts "café" as one word and keeps a Thai vowel mark attached, so
// a narrower class here would bound the word early and drop notes the count already includes.
// Every consumer compiles this with the `u` flag, which the property escapes require.
const LEFT_BOUNDARY = `(?<![${LETTER}])`;
const RIGHT_BOUNDARY = `(?![${LETTER}])`;
const NEVER = "(?!)";

// NFC to match the server, which normalizes before tokenizing: a decomposed "café" must compile to
// the same pattern as the composed one, or the drill-down misses what the count included.
const toPattern = (form: string): string =>
  form.normalize("NFC").trim().split(/\s+/).filter(Boolean).map(escapeRegExp).join(SEPARATOR);

// Longest alternative first, so "patient outcome" highlights the phrase, not the "patient" it
// starts with.
const matchedFormsPattern = (forms: string[]): string => {
  const patterns = uniq(forms.map(toPattern))
    .filter(isNotEmptyString)
    .sort((a, b) => b.length - a.length);
  return patterns.length === 0
    ? NEVER
    : `${LEFT_BOUNDARY}(?:${patterns.join("|")})${RIGHT_BOUNDARY}`;
};

export default matchedFormsPattern;
